import type { TaskFile } from "../types/types";

/**
 * タスク添付ファイル API ユーティリティ（F017）。
 * 認証は既存 API と同様に honbuId クエリパラメータのみ（内部トークン方式の導入時に一括対応する）。
 */

export const buildFileDownloadUrl = (fileId: string, honbuId: number): string =>
  `/api/files/download/${fileId}?honbuId=${honbuId}`;

export const buildFilePreviewUrl = (fileId: string, honbuId: number): string =>
  `/api/files/preview/${fileId}?honbuId=${honbuId}`;

export async function fetchTaskFiles(
  taskId: string | number,
  honbuId: number,
): Promise<TaskFile[]> {
  const res = await fetch(`/api/files/task/${taskId}?honbuId=${honbuId}`);
  if (!res.ok) throw new Error(`添付ファイルの取得に失敗しました (${res.status})`);
  return res.json();
}

export async function uploadTaskFile(
  taskId: string | number,
  honbuId: number,
  file: File,
): Promise<TaskFile> {
  const formData = new FormData();
  formData.append("taskId", String(taskId));
  formData.append("honbuId", String(honbuId));
  formData.append("file", file);

  const res = await fetch("/api/files/upload", {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `アップロードに失敗しました (${res.status})`);
  }
  return res.json();
}

/** タスクからの添付解除（リンク削除のみ。共有先の他タスクには影響しない） */
export async function removeTaskFileLink(
  taskId: string | number,
  fileId: string,
  honbuId: number,
): Promise<void> {
  const res = await fetch(
    `/api/files/${fileId}/tasks/${taskId}?honbuId=${honbuId}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`添付の削除に失敗しました (${res.status})`);
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export type FilePreviewKind = "image" | "video" | "pdf" | "office" | "other";

/** 拡張子でプレビュー方式を判定する（ChatisFiles と同方式） */
export function getFilePreviewKind(fileName: string): FilePreviewKind {
  const name = fileName.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp)$/.test(name)) return "image";
  if (/\.(mp4|webm|ogg|mov|m4v|3gp)$/.test(name)) return "video";
  if (/\.pdf$/.test(name)) return "pdf";
  if (/\.(docx|xlsx|pptx|doc|xls|ppt)$/.test(name)) return "office";
  return "other";
}
